import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from '../../utils/convergence'
import {
  authLogout,
  clearPaymentOptionsError,
  getPaymentOptions,
  selectPurchaseMethod,
  setCheckoutStatus,
  setShowEfaModal,
  useAppDispatch,
  useAppropriatePaymentMethod,
  useAppRouter,
  useConsumerLendingEligible,
  useConsumerLendingState,
  useCountry,
  useIsCreditFreezeDeclineFlow,
  useIsExtendedFraudAlert,
  useIsPclOnlyOfferPage,
  useOfferDetailPBI,
  usePaymentOptionsError,
  usePreselectedPaymentOption,
  usePBIRejected,
  useSelectedPaymentType,
  useShowAffIdvModal,
  useShowEfaModal,
  useShowIdvModal,
  useAddPaymentMethodFlowExperimentTrigger,
  useShouldRoutePi4SwitcherWithNoPaymentMethodToSummary,
  useShouldTriggerPi4SwitcherWithNoPaymentMethodExperiment,
  verifyEFARobocallEvent,
} from '../../state'
import { CheckoutStatus, IdentityVerificationModalKey, PaymentOptionsStatus, VerifyEFARobocallEventRequest } from '../../model'
import { Button, Divider, Heading, Icon, Loader, Text, useIsBreakpointLargeAndUp, useModal } from '@afterpaytouch/core'
import { Country } from '@afterpay/types'
import { PaymentType } from '@afterpay/types'
import { ConsumerWelcome } from '../PaymentTypes/ConsumerWelcome'
import { usePBIRejectedText } from '../PaymentTypes/helper'
import { PaymentTypesPBIRejected } from '../PaymentTypes/PaymentTypesPBIRejected'
import { PaymentOptionsExpandableRadioGroup } from './PaymentOptionsExpandableRadioGroup'
import { PaymentTypesTerms } from '../PaymentTypes/PaymentTypesTerms'
import { Route, useSummaryRoute } from '../../config/router'
import styles from './style.module.css'
import continueButtonStyles from './PaymentOptionsContinueButton/style.module.css'
import paymentTypesStyles from '../PaymentTypes/style.module.css'
import { useCheckoutSizes } from '../../utils/responsive'
import { TrackingEvent } from '../../model/monitoring'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { PaymentOptionsContinueButton } from './PaymentOptionsContinueButton'
import { useHandlePaymentOptionsError, useSelectedPaymentOptionState, useSendEFARobocallEvent } from './hooks'
import { EfaRobocallModal } from '../EfaRobocallModal'
import { IdentityVerificationModal } from '../IdentityVerificationModal'
import { handlePaymentOptionData } from './utils'

const PBIRejectedRadioMessage = (): JSX.Element => {
  const { smallTextSize } = useCheckoutSizes()
  const rejectedTextPBI = usePBIRejectedText()

  return (
    <div className={paymentTypesStyles.PBIDeclineWrapper}>
      <span className={paymentTypesStyles.rejectedInfoIcon}>
        <Icon.Info size='ExtraSmall' />
      </span>
      <span className={paymentTypesStyles.rejectedText}>
        <Text size={smallTextSize}>{rejectedTextPBI}</Text>
      </span>
    </div>
  )
}

/**
 * Inline payment plans variant (PAYMENT_TYPE_SORTING feature flag).
 * Merges payment type selection (Pay Monthly / Pay in 4) with inline loading and
 * payment plans—no full-page transition. When PCL is selected, fetches options
 * and shows inline loader then plans; PBI path uses continue button to summary.
 */
export const PaymentOptionsExpandable: FunctionComponent = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const country = useCountry()
  const preselectedPaymentOption = usePreselectedPaymentOption()
  const selectedPaymentType = useSelectedPaymentType()
  const [lock, setLock] = useState<boolean>(false)
  const { paymentOptionsStatus, paymentOptions } = useConsumerLendingState()
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const [selectedPaymentOption, setSelectedPaymentOption] = useSelectedPaymentOptionState()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const isDesktop = useIsBreakpointLargeAndUp()
  const summaryRoute = useSummaryRoute()
  const [paymentOptionsUpdated, setPaymentOptionsUpdated] = useState(false)
  const [pbiOptionSelected, setPbiOptionSelected] = useState<boolean>(false)
  const [continueClicked, setContinueClicked] = useState<boolean>(false)
  const [idvCompleted, setIdvCompleted] = useState<boolean>(false)
  // US users with PBI rejected see <PaymentTypesPBIRejected /> first. Once they acknowledge it,
  // skip the early return so the inline PCL flow can run on this same page instead of
  // `router.push`-ing to the page they're already on (which would hang the Continue spinner).
  const [pbiRejectedAcknowledged, setPbiRejectedAcknowledged] = useState<boolean>(false)
  const paymentMethod = useAppropriatePaymentMethod()
  const paymentOptionsError = usePaymentOptionsError()
  const summaryFirstSelectedPaymentType = pbiOptionSelected ? PaymentType.PBI : selectedPaymentOption?.paymentType
  const shouldRoutePi4SwitcherWithNoPaymentMethodToSummary = useShouldRoutePi4SwitcherWithNoPaymentMethodToSummary(summaryFirstSelectedPaymentType)
  const shouldTriggerPi4SwitcherWithNoPaymentMethodExperiment = useShouldTriggerPi4SwitcherWithNoPaymentMethodExperiment(summaryFirstSelectedPaymentType)
  const triggerAddPaymentMethodFlowExperiment = useAddPaymentMethodFlowExperimentTrigger()
  const { handlePaymentOptionsError } = useHandlePaymentOptionsError()
  const isExtendedFraudAlert = useIsExtendedFraudAlert()
  const isCreditFreezeDeclineFlow = useIsCreditFreezeDeclineFlow()
  const hasPBIRejected = usePBIRejected()
  const isPclOnlyOfferPage = useIsPclOnlyOfferPage()
  const offerDetailPBI = useOfferDetailPBI()
  const {
    onSuccess: sendEFARobocallEventSuccess,
    onError: sendEFARobocallEventError,
    isSuccess: isEFARobocallEventSuccess,
    isDecline: isEFARobocallEventDecline,
  } = useSendEFARobocallEvent()
  const showEfaModal = useShowEfaModal()
  const shouldShowEfaModal = isExtendedFraudAlert && showEfaModal
  const showIDVModal = useShowIdvModal()
  const showAffIdvModal = useShowAffIdvModal()
  const { showModal: showIdvModal, closeModal } = useModal(IdentityVerificationModalKey)

  const preSelectedEventValue = useMemo<string>(() => {
    if (preselectedPaymentOption?.paymentType === PaymentType.PBI) {
      return 'PBI'
    }
    if (preselectedPaymentOption?.paymentType === PaymentType.PCL) {
      return `PCL-${preselectedPaymentOption?.installmentsCount}M`
    }
    return 'None'
  }, [preselectedPaymentOption])

  const sendEFARobocallEvent = async (request: VerifyEFARobocallEventRequest): Promise<void> => {
    await dispatch(verifyEFARobocallEvent(request))
  }

  useEffect(() => {
    router.prefetch(Route.SUMMARY)
    router.prefetch(Route.ADD_PAYMENT_METHOD)
    router.prefetch(Route.ENTER_EMAIL)
  }, [router])

  useEffect(() => {
    if (hasPBIRejected) {
      logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: offerDetailPBI?.rejectedReasonCode })
    }
  }, [hasPBIRejected, offerDetailPBI?.rejectedReasonCode, logEvent])

  useEffect(() => {
    if (isExtendedFraudAlert && !isEFARobocallEventSuccess) {
      sendEFARobocallEvent({
        onSuccess: sendEFARobocallEventSuccess,
        onError: sendEFARobocallEventError,
      })
    }
  }, [isExtendedFraudAlert, isEFARobocallEventSuccess])

  useEffect(() => {
    if (continueClicked && selectedPaymentType === PaymentType.PCL && showIDVModal && !idvCompleted) {
      showIdvModal()
    }
  }, [continueClicked, selectedPaymentType, showIDVModal, idvCompleted])

  useEffect(() => {
    const doGetPaymentOptions = async (): Promise<void> => {
      try {
        await dispatch(getPaymentOptions()).unwrap()
      } catch (e) {
        handlePaymentOptionsError(e)
      }
    }

    // Only dispatch when status is Initial — prevents retry loops that hammer the API and trigger 429s
    // when the request transitions to Fetching, Error, or returns Fetched.
    const shouldFetchPCL =
      (selectedPaymentType === PaymentType.PCL || preselectedPaymentOption?.paymentType === PaymentType.PCL) &&
      (continueClicked || isExtendedFraudAlert) &&
      (idvCompleted || !showIDVModal) &&
      (isEFARobocallEventSuccess || !isExtendedFraudAlert) &&
      paymentOptionsStatus === PaymentOptionsStatus.Initial
    const shouldLoadPaymentOptionsAU = isConsumerLendingEligible && country === 'AU'
    const shouldLoadPaymentOptionsUS = isConsumerLendingEligible && country === 'US'

    if (shouldFetchPCL && (shouldLoadPaymentOptionsAU || shouldLoadPaymentOptionsUS)) {
      doGetPaymentOptions()
    }
  }, [
    selectedPaymentType,
    preselectedPaymentOption?.paymentType,
    continueClicked,
    idvCompleted,
    showIDVModal,
    isEFARobocallEventSuccess,
    isExtendedFraudAlert,
    paymentOptionsStatus,
    isConsumerLendingEligible,
    country,
    dispatch,
    handlePaymentOptionsError,
  ])

  useEffect(() => {
    if (paymentOptionsStatus === PaymentOptionsStatus.Fetched) {
      logEvent(TrackingEvent.VIEWED_CL_PAYMENT_OPTIONS_PAGE, {
        paymentOption: handlePaymentOptionData(paymentOptions),
        preSelected: preSelectedEventValue,
      })
    }
  }, [preSelectedEventValue, paymentOptionsStatus, paymentOptions, logEvent])

  useEffect(() => {
    if (paymentOptionsStatus === PaymentOptionsStatus.Fetched) {
      dispatch(clearPaymentOptionsError())
    }
  }, [paymentOptionsStatus, dispatch])

  useEffect(() => {
    const logoutUser = async (): Promise<void> => {
      await dispatch(authLogout())
      router.push(Route.ENTER_EMAIL)
    }

    if (isEFARobocallEventDecline) {
      logoutUser()
    }

    if (isEFARobocallEventSuccess) {
      dispatch(setShowEfaModal(false))
    }
  }, [isEFARobocallEventSuccess, isEFARobocallEventDecline, dispatch, router])

  useEffect(() => {
    if (shouldShowEfaModal || isCreditFreezeDeclineFlow) {
      return
    }
    if (pbiOptionSelected || paymentOptionsUpdated || paymentOptionsError !== undefined) {
      if (paymentMethod !== null) {
        dispatch(selectPurchaseMethod(paymentMethod, false))
      } else {
        if (
          (pbiOptionSelected || (paymentOptionsUpdated && selectedPaymentOption?.paymentType === PaymentType.PBI)) &&
          shouldTriggerPi4SwitcherWithNoPaymentMethodExperiment
        ) {
          triggerAddPaymentMethodFlowExperiment({ source: 'cl_entry_pi4_switcher', selectedPaymentType: PaymentType.PBI })
          if (shouldRoutePi4SwitcherWithNoPaymentMethodToSummary) {
            router.push(summaryRoute)
            return
          }
        }
        dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
        router.push(Route.ADD_PAYMENT_METHOD)
        return
      }
      if (pbiOptionSelected || paymentOptionsUpdated || paymentOptionsError !== undefined) {
        router.push(summaryRoute)
      }
    }
    // Intentionally omit paymentMethod, summaryRoute, dispatch, router to avoid re-running after selectPurchaseMethod updates state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pbiOptionSelected, paymentOptionsUpdated, paymentOptionsError, shouldShowEfaModal, isCreditFreezeDeclineFlow])

  useEffect(() => {
    if (preselectedPaymentOption != null) {
      setSelectedPaymentOption(preselectedPaymentOption)
    }
  }, [preselectedPaymentOption, setSelectedPaymentOption])

  // A PCL_ONLY decision hides PBI from this surface entirely (the server ruled it out
  // non-recoverably), so the PBI-rejection interstitial and the inline explainer under the
  // radio group are suppressed with it. Decline UX for the one offer page is deferred to
  // CASHDAP-3449 states 4-5.
  const showPBIRejectedView = hasPBIRejected && !isPclOnlyOfferPage && country === Country.US && !pbiRejectedAcknowledged
  const shouldShowEfaFullPageLoader = isExtendedFraudAlert && paymentOptionsStatus < PaymentOptionsStatus.Fetched

  if (shouldShowEfaFullPageLoader) {
    return (
      <div {...(isDesktop && { className: styles.paymentOptionsLoadingWrapperDesktop })}>
        {shouldShowEfaModal && <EfaRobocallModal isError={shouldShowEfaModal} />}
        <Heading size='XL'>{t('consumerLending:loadPaymentOptions:title')}</Heading>
        <div className={styles.loadPaymentOptionsText}>
          <Text>{t('consumerLending:loadPaymentOptions:message')}</Text>
        </div>
        <Loader />
      </div>
    )
  }

  if (showPBIRejectedView) {
    return (
      <PaymentTypesPBIRejected
        onContinue={() => {
          setPbiRejectedAcknowledged(true)
          setContinueClicked(true)
        }}
      />
    )
  }

  const hasSelectedType = selectedPaymentType === PaymentType.PCL || selectedPaymentType === PaymentType.PBI
  const showInitialContinue = hasSelectedType && !continueClicked
  const showPaymentOptionsContinueButton = continueClicked && hasSelectedType

  const handleInitialContinue = (): void => {
    logEvent(TrackingEvent.PRESSED_CL_ENTRY_PAGE_CONTINUE_BUTTON)
    // PBI navigates straight to summary (or AFF IDV modal). Skip expanding the radio group so the
    // user keeps seeing the same compact view until the route changes — no flash of empty/skeleton
    // content. PCL still expands to fetch and render plans inline.
    if (selectedPaymentType === PaymentType.PBI) {
      if (showAffIdvModal) {
        showIdvModal()
      } else {
        setPbiOptionSelected(true)
      }
      return
    }
    setContinueClicked(true)
  }

  return (
    <>
      <ConsumerWelcome />
      <Divider />
      <div className={isDesktop ? styles.paymentOptionsWrapperDesktop : styles.paymentOptionsWrapperMobile}>
        <PaymentOptionsExpandableRadioGroup
          lock={lock}
          selectedPaymentOptionLocal={selectedPaymentOption}
          setSelectedPaymentOptionLocal={setSelectedPaymentOption}
          expanded={continueClicked}
        />
        {hasPBIRejected && !isPclOnlyOfferPage && <PBIRejectedRadioMessage />}
        {!continueClicked && <PaymentTypesTerms />}
        {showInitialContinue && (
          <div className={continueButtonStyles.continueButton}>
            <Button
              padding='Fluid'
              onClick={handleInitialContinue}
              testNameSpace='payment-types'
              loading={paymentOptionsStatus === PaymentOptionsStatus.Fetching}
            >
              {t('consumerLending:paymentOptions:continue')}
            </Button>
          </div>
        )}
        {showPaymentOptionsContinueButton && (
          <PaymentOptionsContinueButton
            lock={lock}
            setLock={setLock}
            selectedPaymentOptionLocal={selectedPaymentOption}
            setPaymentOptionsUpdated={setPaymentOptionsUpdated}
          />
        )}
        <IdentityVerificationModal
          setClicked={(value) => {
            if (!value) setContinueClicked(false)
          }}
          setPbiOptionSelected={setPbiOptionSelected}
          onVerified={() => {
            closeModal()
            setIdvCompleted(true)
          }}
        />
      </div>
    </>
  )
}
